import * as d3 from 'd3';
import * as d3Selection from 'd3-selection';

import { formatTime, stripZone } from './time_utils';
import D3Tooltip from './d3_tooltip';

export const RECT_RADIUS_X = 8;
export const RECT_RADIUS_Y = 100;
export const AXIS_COLOR = '#9ca3af';
export const NOW_BG_COLOR = '#9ca3af';
export const NOW_LINE_OVERFLOW = 6;
export const CHART_HEIGHT = 16;
export const LOCATION_CHART_HEIGHT = 6;
export const LOCATION_LINE_OFFSET = 10;
export const CHART_PADDING_BOTTOM = 20;
export const CHART_PADDING_TOP = NOW_LINE_OVERFLOW;
export const CHART_BG_COLOR = '#e5e7eb';
export const NEW_FILL_COLOR = '#ffffff';
export const NEW_STROKE_WIDTH = 1;
export const NEW_STROKE_COLOR = '#2f8af4';
export const NEW_DEFAULT_MINUTES = 60;
export const NEW_LINE_LENGTH = CHART_HEIGHT + CHART_PADDING_TOP - 14;
export const NEW_TIME_ENTRY_RECT_CLASS = 'new-time-entry';
export const NEW_MINIMUM_ENTRY_MINUTES = 3;
export const DELETE_TIME_COLOR = '#f05252';
export const DEFAULT_COLOR = 'salmon';
export const HOUR_TICK_FORMAT_12 = 'ha';
export const HOUR_TICK_FORMAT_24 = 'HH';
export const TICK_FORMATS = {
  '12hr': HOUR_TICK_FORMAT_12,
  '24hr': HOUR_TICK_FORMAT_24,
};

export function drawTimeline(svg, { now, width, start, stop, ticks = [6, 12, 18], tickFormat = '12hr', paddingTop = CHART_PADDING_TOP }) {
  const date = stripZone(moment(start).startOf('day').set({ minute: 0, seconds: 0 }));

  start = moment(start);
  stop = moment(stop);
  ticks = ticks.map((hour) => moment(date).set({ hour }));

  const elTooltip = d3Selection.local();

  svg.selectAll('*').remove();

  svg.attr('width', width).attr('height', paddingTop + CHART_HEIGHT + CHART_PADDING_BOTTOM);

  const xScale = d3.scaleUtc().domain([start.toDate(), stop.toDate()]).range([0, width]);
  const xAxis = d3
    .axisBottom()
    .scale(xScale)
    .tickFormat((d) => d.format(TICK_FORMATS[tickFormat]))
    .tickValues(ticks);
  const chart = svg.append('g').attr('class', 'chart');
  const bg = chart
    .append('g')
    .attr('class', 'bg')
    .insert('rect')
    .attr('y', paddingTop)
    .attr('x', 0)
    .attr('height', CHART_HEIGHT)
    .attr('width', width)
    .attr('rx', RECT_RADIUS_X)
    .attr('ry', RECT_RADIUS_Y)
    .attr('fill', CHART_BG_COLOR);
  let nowGroup, nowBgEndcap, nowStartLine, nowBg, nowTooltip;

  if (now && now.isBetween(start, stop)) {
    nowGroup = chart.append('g').attr('class', 'now');

    // eslint-disable-next-line no-unused-vars
    nowBgEndcap = nowGroup
      .insert('rect')
      .attr('y', paddingTop)
      .attr('x', () => {
        return xScale(now);
      })
      .attr('height', CHART_HEIGHT)
      .attr('width', RECT_RADIUS_X)
      .attr('fill', NOW_BG_COLOR);

    // eslint-disable-next-line no-unused-vars
    nowStartLine = nowGroup
      .insert('line')
      .attr('class', 'now-line')
      .attr('stroke', NOW_BG_COLOR)
      .attr('stroke-width', 2)
      .attr('x1', () => {
        return xScale(now);
      })
      .attr('x2', () => {
        return xScale(now);
      })
      .attr('y1', 0)
      .attr('y2', paddingTop + CHART_HEIGHT + 6);

    // eslint-disable-next-line no-unused-vars
    nowBg = nowGroup
      .insert('rect')
      .attr('y', paddingTop)
      .attr('x', () => {
        return xScale(now);
      })
      .attr('height', CHART_HEIGHT)
      .attr('width', () => {
        const width = xScale(stop) - xScale(now);

        if (width > 0) {
          return width;
        }

        return 0;
      })
      .attr('rx', RECT_RADIUS_X)
      .attr('ry', RECT_RADIUS_Y)
      .attr('fill', NOW_BG_COLOR)
      .each(function () {
        if (!elTooltip.get(this)) {
          const formattedTime = formatTime(now, { showSeconds: false });
          const tooltip = new D3Tooltip(this, `${formattedTime} - (Future)`, { placement: 'top-start' });

          elTooltip.set(this, tooltip);
          nowTooltip = tooltip;
        }
      });
  }

  const xAxisGroup = svg
    .append('g')
    .attr('class', 'x axis')
    .attr('transform', `translate(0, ${CHART_HEIGHT + paddingTop})`)
    .call(xAxis)
    .call((graph) => {
      graph.select('.domain').remove();
      graph.selectAll('.tick line').attr('stroke', CHART_BG_COLOR);
      graph.selectAll('.tick text').attr('fill', AXIS_COLOR);
    });

  return {
    bg,
    chart,
    elTooltip,
    nowGroup,
    nowBg,
    nowBgEndcap,
    nowStartLine,
    nowTooltip,
    xAxis,
    xAxisGroup,
    xScale,
  };
}
